/**
 * Goal Setting Service
 * Handles API calls for strategy goals
 */

import api from './api';

export interface Goal {
    id: number;
    goal_type: string;
    goal_category: string;
    cycle_start_date: string;
    cycle_end_date: string;
    is_locked: boolean;
    baseline_value: string;
    current_value: string;
    target_value: string;
    unit: string;
    target_type: string;
    slab_data: Array<{
        label: string;
        percentage: number;
        color: string;
    }> | null;
    serp_features_breakdown?: Array<{
        type: string;
        keyword: string;
    }>;
    progress_percentage: number;
    last_calculated_at: string | null;
    created_at: string | null;
    updated_at: string | null;
}

export interface GoalsResponse {
    status: string;
    message: string;
    user_id: string;
    data: {
        goals: Goal[];
        can_refresh: boolean;
    };
}

export interface InitializeResponse {
    status: string;
    message: string;
    user_id: string;
    data: {
        success: boolean;
        goals_created: number;
        goal_types: string[];
        cycle_start: string;
        cycle_end: string;
    };
}

class GoalService {
    /**
     * Fetch all active goals for a user
     */
    async fetchGoals(userId: string): Promise<GoalsResponse> {
        return api.get<GoalsResponse>(`/strategy/goals?user_id=${encodeURIComponent(userId)}`);
    }

    /**
     * Initialize goals after onboarding
     */
    async initializeGoals(userId: string): Promise<InitializeResponse> {
        return api.post<InitializeResponse>(`/strategy/goals/initialize?user_id=${encodeURIComponent(userId)}`);
    }

    /**
     * Refresh goals (create new 90-day cycle)
     */
    async refreshGoals(userId: string): Promise<InitializeResponse> {
        return api.post<InitializeResponse>(`/strategy/goals/refresh?user_id=${encodeURIComponent(userId)}`);
    }
}

// Export singleton instance
export const goalService = new GoalService();
export default goalService;
